import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import Account from "./components/Account";
import AdminUsers from "./components/AdminUsers";
import TrialEndedScreen from "./components/TrialEndedScreen";
import { AuthUser, UserSession } from "./types";
import "./App.css";

type Page = "dashboard" | "account" | "admin-users";

function App() {
  const [userSession, setUserSession] = useState<UserSession>({
    isAuthenticated: false,
    user: null,
  });
  const [currentPage, setCurrentPage] = useState<Page>("dashboard");
  const [activeSection, setActiveSection] = useState<string>("");
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Check for OAuth callback in URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");

    // Enhanced popup window detection - check multiple conditions
    const isPopupWindow = () => {
      // Check if we have a parent window
      if (window.opener && window.opener !== window) {
        return true;
      }

      // Check if window name suggests it's a popup (set when opening auth window)
      if (window.name === "googleAuth") {
        return true;
      }

      // Check if we're in an iframe or popup context
      if (window.parent !== window) {
        return true;
      }

      // Check window size - auth popups are typically small
      if (window.outerWidth <= 600 && window.outerHeight <= 700) {
        return true;
      }

      return false;
    };

    // If this is a popup window with auth callback, send message to parent
    if (isPopupWindow() && (code || error)) {
      console.log("Popup window detected with auth result, notifying parent");

      try {
        const targetOrigin = window.location.origin;
        const message = error
          ? {
              type: "OAUTH_ERROR",
              error: error,
            }
          : {
              type: "OAUTH_SUCCESS",
              code: code,
              state: state,
            };

        // Try multiple ways to notify the parent
        if (window.opener && window.opener !== window) {
          console.log("Sending message to opener window");
          window.opener.postMessage(message, targetOrigin);
        }

        if (window.parent && window.parent !== window) {
          console.log("Sending message to parent window");
          window.parent.postMessage(message, targetOrigin);
        }

        // Close the popup after a short delay to ensure message is sent
        setTimeout(() => {
          console.log("Closing popup window after auth result");
          window.close();
        }, 100);
      } catch (err) {
        console.error("Error communicating with parent window:", err);
        // Fallback: close window anyway
        window.close();
      }

      return;
    }

    // Load stored user session from localStorage
    const stored = localStorage.getItem("user_session");
    if (stored) {
      try {
        const parsedSession = JSON.parse(stored);
        // For secure authentication, we only check if user exists
        // Token validity is managed server-side
        if (parsedSession.user) {
          setUserSession(parsedSession);

          // Refresh user data from server to get latest isAdmin status and other updates
          fetch(`/api/user/${parsedSession.user.id}`, {
            credentials: "include",
          })
            .then((res) => {
              if (res.ok) {
                return res.json();
              }
              // If 401, session is invalid - clear cached data and require re-login
              if (res.status === 401) {
                console.warn("Session expired (401), clearing cached session");
                localStorage.removeItem("user_session");
                setUserSession({
                  isAuthenticated: false,
                  user: null,
                });
                throw new Error("Session expired");
              }
              console.error("Failed to refresh user data, status:", res.status);
              throw new Error(`Failed to refresh user data: ${res.status}`);
            })
            .then((userData) => {
              // Update session with fresh server data
              const refreshedSession = {
                isAuthenticated: true,
                user: userData,
              };
              setUserSession(refreshedSession);
              localStorage.setItem(
                "user_session",
                JSON.stringify(refreshedSession),
              );
              console.log(
                "User session refreshed from server, isAdmin:",
                userData.isAdmin,
              );
            })
            .catch((err) => {
              console.error(
                "Could not refresh user data. Error:",
                err,
              );
              // Only use cached session if error is NOT a 401 (session expired)
              if (err.message !== "Session expired") {
                console.warn(
                  "Using cached session isAdmin:",
                  parsedSession.user.isAdmin,
                );
              }
            });
        } else {
          // Remove invalid session
          localStorage.removeItem("user_session");
        }
      } catch (err) {
        // Invalid stored data
        localStorage.removeItem("user_session");
      }
    }
  }, []);

  // Dynamically set header offset based on actual header height
  useLayoutEffect(() => {
    if (!headerRef.current) return;

    const setOffset = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty(
          "--header-offset",
          `${height}px`,
        );
      }
    };

    setOffset(); // Set initial value

    const resizeObserver = new ResizeObserver(setOffset);
    resizeObserver.observe(headerRef.current);
    window.addEventListener("resize", setOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", setOffset);
    };
  }, [userSession.isAuthenticated]); // Re-run when authentication state changes (header shows/hides navigation)

  // Scroll detection for active navigation state (only on dashboard)
  useEffect(() => {
    if (!userSession.isAuthenticated || currentPage !== "dashboard") return;

    const handleScroll = () => {
      const sections = [
        "configuration",
        "insights",
        "topic-clusters",
        "link-gaps",
        "content-gaps",
        "historical-rankings",
        "search-intent",
        "rich-features",
        "ai-searches",
        "questions",
        "query-performance",
        "position-analysis",
        "url-inspector",
        "non-indexed-urls",
      ];

      let currentActive = "";
      const scrollPosition = window.scrollY + 100; // Add offset for header

      // Find which section is currently in view
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = window.scrollY + rect.top;

          if (scrollPosition >= elementTop) {
            currentActive = sectionId;
          }
        }
      }

      setActiveSection(currentActive);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [userSession.isAuthenticated, currentPage]);

  const handleAuthSuccess = (authUser: AuthUser) => {
    const newSession: UserSession = {
      isAuthenticated: true,
      user: authUser.user,
    };
    setUserSession(newSession);
    // Store ONLY user info - NO CREDENTIALS for security
    localStorage.setItem(
      "user_session",
      JSON.stringify({
        isAuthenticated: true,
        user: authUser.user,
      }),
    );
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to destroy session
      const response = await fetch("/api/logout", {
        method: "POST",
        credentials: "include", // Include cookies for session
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.warn(
          "Backend logout failed, but proceeding with frontend cleanup",
        );
      } else {
        console.log("Backend session destroyed successfully");
      }
    } catch (error) {
      console.error("Logout error:", error);
      console.warn(
        "Backend logout failed, but proceeding with frontend cleanup",
      );
    }

    // Clear frontend state and localStorage
    setUserSession({
      isAuthenticated: false,
      user: null,
    });
    setCurrentPage("dashboard"); // Reset to dashboard on logout
    localStorage.removeItem("user_session");

    console.log("Frontend logout completed");
  };

  const handleNavigateToAccount = () => {
    setCurrentPage("account");
  };

  const handleNavigateToDashboard = () => {
    setCurrentPage("dashboard");
  };

  const handleNavigateToAdminUsers = () => {
    setCurrentPage("admin-users");
  };

  const isTrialExpired = Boolean(
    userSession.user &&
    userSession.user.subscriptionStatus === "expired" &&
    userSession.user.trialEndsAt &&
    !userSession.user.isAdmin
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        ref={headerRef}
        className={`bg-white shadow-sm border-b border-gray-200 ${userSession.isAuthenticated ? "sticky top-0 z-50" : ""}`}
      >
        <div className="w-[90vw] mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center py-4 ${userSession.isAuthenticated ? "justify-between" : "justify-center"}`}
          >
            <div className="flex items-center">
              <img
                src="/serprocket-logo.png"
                alt="Serprocket SEO Console"
                className="h-12 w-auto object-contain"
                onError={(e) => {
                  console.log(
                    "Serprocket logo failed to load, showing fallback",
                  );
                  e.currentTarget.style.display = "none";
                  // Show text logo fallback
                  const fallback = e.currentTarget
                    .nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              <div className="hidden items-center">
                <div className="w-10 h-10 mr-3 bg-gradient-to-r from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">S</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                    Serprocket
                  </h1>
                  <p className="text-xs text-gray-500 -mt-1">SEO Console</p>
                </div>
              </div>
            </div>
            {userSession.isAuthenticated && (
              <div className="flex items-center space-x-3">
                {userSession.user?.isAdmin && (
                  <button
                    onClick={handleNavigateToAdminUsers}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition duration-200 ${
                      currentPage === "admin-users"
                        ? "bg-gradient-to-r from-orange-400 to-pink-600 text-white"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    Users
                  </button>
                )}
                <button
                  onClick={handleNavigateToAccount}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition duration-200 ${
                    currentPage === "account"
                      ? "bg-gradient-to-r from-orange-400 to-pink-600 text-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Account
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* Dashboard Navigation Menu - Only shown when logged in and on dashboard */}
          {userSession.isAuthenticated && currentPage === "dashboard" && !isTrialExpired && (
            <nav className="pb-4">
              <div className="flex flex-wrap gap-2 text-sm">
                {[
                  {
                    id: "configuration",
                    label: "Configuration",
                    desc: "Data setup",
                  },
                  {
                    id: "insights",
                    label: "Insights",
                    desc: "SEO opportunities",
                  },
                  {
                    id: "topic-clusters",
                    label: "Topic Clusters",
                    desc: "Keyword grouping",
                  },
                  {
                    id: "link-gaps",
                    label: "Link Gaps",
                    desc: "Anchor opportunities",
                  },
                  {
                    id: "content-gaps",
                    label: "Content Gaps",
                    desc: "Sitemap analysis",
                  },
                  {
                    id: "historical-rankings",
                    label: "Historical Rankings",
                    desc: "6-month position trends",
                  },
                  {
                    id: "search-intent",
                    label: "Search Intent",
                    desc: "4-intent analysis",
                  },
                  {
                    id: "rich-features",
                    label: "Snippets & AI Overviews",
                    desc: "Featured snippets and AI Overview appearances",
                  },
                  {
                    id: "ai-searches",
                    label: "AI Searches",
                    desc: "Potential AI-referred traffic",
                  },
                  {
                    id: "questions",
                    label: "Questions",
                    desc: "Question-based queries",
                  },
                  {
                    id: "query-performance",
                    label: "Query Data",
                    desc: "Detailed table",
                  },
                  {
                    id: "position-analysis",
                    label: "Position Data",
                    desc: "Ranking analysis",
                  },
                  {
                    id: "url-inspector",
                    label: "URL Inspector",
                    desc: "Inspect individual URLs",
                  },
                  {
                    id: "non-indexed-urls",
                    label: "Non-indexed URLs",
                    desc: "Review sitemap indexing status",
                  },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      const element = document.getElementById(item.id);
                      if (element) {
                        element.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 border ${
                      activeSection === item.id
                        ? "bg-gradient-to-r from-orange-400 to-pink-600 text-white border-pink-600"
                        : "text-gray-600 bg-gray-50 hover:bg-gradient-to-r hover:from-orange-400 hover:to-pink-600 hover:text-white border-gray-200 hover:border-pink-600"
                    }`}
                    title={item.desc}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="w-[90vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {userSession.isAuthenticated ? (
          <>
            {currentPage === "dashboard" && (
              isTrialExpired ? (
                <TrialEndedScreen user={userSession.user!} />
              ) : (
                <Dashboard
                  userId={userSession.user!.id}
                  user={userSession.user!}
                  onNavigateToAccount={handleNavigateToAccount}
                />
              )
            )}
            {currentPage === "account" && (
              <Account
                userId={userSession.user!.id}
                user={userSession.user!}
                onNavigateBack={handleNavigateToDashboard}
              />
            )}
            {currentPage === "admin-users" && userSession.user?.isAdmin && (
              <AdminUsers
                userId={userSession.user!.id}
                onNavigateBack={handleNavigateToDashboard}
              />
            )}
          </>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-gray-900 text-3xl font-bold mb-2">
                Find out how well your website is working.
              </h1>
              <h2 className="text-gray-600 text-lg">
                See what your website is really ranking for, and how it's
                performing.
              </h2>
            </div>
            <Auth onAuthSuccess={handleAuthSuccess} userSession={userSession} />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="w-[90vw] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 text-sm">
            SEO Dashboard for GSC data • Built by{" "}
            <a
              href="https://searchworthy.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Searchworthy
            </a>
            {" "}• Visit App website:{" "}
            <a
              href="https://serprocket.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Serprocket
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
